<template>
  <div class="container-manager" :class="{ collapsed: isCollapsed }">
    <div class="collapse-btn" @click="toggleCollapse">
      <i :class="isCollapsed ? 'el-icon-arrow-left' : 'el-icon-arrow-right'"></i>
    </div>
    <div class="container-status" v-show="!isCollapsed">
      <div class="status-header">
        <div class="status-info">
          <span class="status-icon" :class="statusClass">
            {{ statusIcon }}
          </span>
          <span class="status-text">{{ statusText }}</span>
        </div>
        <div class="status-actions">
          <template v-if="!podInfo">
            <el-button
              type="primary"
              size="small"
              @click="createPod"
              :loading="loading"
            >
              启动云电脑
            </el-button>
          </template>
          <template v-else>
            <el-dropdown trigger="click" @command="handleCommand">
              <el-button type="primary" size="small">
                云电脑操作
                <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item 
                  command="vnc"
                  :disabled="!(podInfo.access_info?.vnc?.novnc_url || podInfo.vnc_url)"
                >
                  <i class="el-icon-monitor"></i> 查看云电脑
                </el-dropdown-item>
                <el-dropdown-item 
                  command="copyVnc"
                  :disabled="!(podInfo.access_info?.vnc?.novnc_url || podInfo.vnc_url)"
                >
                  <i class="el-icon-copy-document"></i> 复制VNC链接
                </el-dropdown-item>
                <el-dropdown-item 
                  command="copySsh"
                  :disabled="!(podInfo.access_info?.ssh?.command || podInfo.ssh_command)"
                >
                  <i class="el-icon-connection"></i> 复制SSH命令
                </el-dropdown-item>
                <el-dropdown-item divided command="restart">
                  <i class="el-icon-refresh"></i> 重启云电脑
                </el-dropdown-item>
                <el-dropdown-item command="stop">
                  <i class="el-icon-video-pause"></i> 停止云电脑
                </el-dropdown-item>
                <el-dropdown-item command="delete" divided>
                  <span style="color: #f56c6c;">
                    <i class="el-icon-delete"></i> 删除云电脑
                  </span>
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
            <el-button
              size="small"
              circle
              @click="refreshStatus"
              :loading="loading"
              title="刷新状态"
            >
              <i class="el-icon-refresh"></i>
            </el-button>
          </template>
        </div>
      </div>
      
      <transition name="fade">
        <div v-if="podInfo" class="pod-details">
          <div class="detail-item">
            <span class="detail-label">云电脑名称：</span>
            <span class="detail-value">{{ podInfo.name }}</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">状态：</span>
            <span class="detail-value" :class="'status-' + podStatus">
              {{ podStatus }}
            </span>
          </div>
        <div class="detail-item" v-if="podInfo.resources">
          <span class="detail-label">资源配置：</span>
          <span class="detail-value">
            CPU: {{ podInfo.resources.cpu || 'N/A' }} | 
            内存: {{ podInfo.resources.memory || 'N/A' }} | 
            存储: {{ podInfo.resources.storage || 'N/A' }}
          </span>
        </div>
        <div class="detail-item" v-if="vncPassword || podInfo.vnc_password || podInfo.access_info?.credentials?.password">
          <span class="detail-label">云电脑密码：</span>
          <span class="detail-value">
            {{ vncPassword || podInfo.vnc_password || '使用创建时提供的密码' }}
            <el-button 
              type="text" 
              size="mini" 
              @click="copyToClipboard(vncPassword || podInfo.vnc_password, '云电脑密码已复制')"
              style="margin-left: 10px;"
              v-if="vncPassword || podInfo.vnc_password"
            >
              <i class="el-icon-copy-document"></i> 复制
            </el-button>
          </span>
        </div>
        <div class="detail-item" v-if="podInfo.access_info?.vnc?.novnc_url || podInfo.vnc_url">
          <span class="detail-label">云电脑访问：</span>
          <span class="detail-value" style="font-size: 12px;">
            <el-button type="primary" size="mini" @click="openVNC">
              <i class="el-icon-monitor"></i> 查看工作台
            </el-button>
            <el-button type="text" size="mini" @click="copyVNCLink">
              <i class="el-icon-copy-document"></i> 复制链接
            </el-button>
          </span>
        </div>
        <div class="detail-item" v-if="podInfo.access_info?.ssh?.command || podInfo.ssh_command">
          <span class="detail-label">SSH访问：</span>
          <span class="detail-value" style="font-size: 12px; word-break: break-all;">
            <code>{{ podInfo.access_info?.ssh?.command || podInfo.ssh_command }}</code>
            <el-button 
              type="text" 
              size="mini" 
              @click="copySSHCommand"
              style="margin-left: 10px;"
            >
              <i class="el-icon-copy-document"></i> 复制
            </el-button>
          </span>
        </div>
        <div class="detail-item" v-if="podInfo.created_at || podInfo.start_time">
          <span class="detail-label">创建时间：</span>
          <span class="detail-value">
            {{ formatTime(podInfo.created_at || podInfo.start_time) }}
          </span>
        </div>
      </div>
      </transition>
      
      <transition name="fade">
        <div v-if="errorMessage" class="error-message">
          <i class="el-icon-warning"></i>
          {{ errorMessage }}
        </div>
      </transition>
    </div>
    <!-- 收起状态下的简化显示 -->
    <div class="collapsed-content" v-show="isCollapsed">
      <div class="collapsed-status">
        <span class="status-icon" :class="statusClass">{{ statusIcon }}</span>
        <span class="collapsed-text">云电脑管理</span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ContainerManager',
  data() {
    return {
      podInfo: null,
      loading: false,
      errorMessage: '',
      checkInterval: null,
      apiUrl: 'https://api.vnc.service.thinkgs.cn',
      apiToken: '',
      vncPassword: '', // 存储VNC密码
      isCollapsed: false, // 收起/展开状态
      lastCheckTime: 0, // 上次检查时间
      checkIntervalTime: 30000, // 默认30秒检查间隔
      cachedPodInfo: null, // 缓存Pod信息
      lastSuccessfulFetch: 0 // 上次成功获取数据的时间
    };
  },
  computed: {
    // 获取Pod的实际状态（兼容列表和详情两种数据格式）
    podStatus() {
      if (!this.podInfo) return null;
      // 详情API返回phase，列表API返回status
      return this.podInfo.phase || this.podInfo.status;
    },
    statusClass() {
      if (this.loading) return 'loading';
      if (this.podStatus) {
        switch(this.podStatus) {
          case 'Running': return 'running';
          case 'Pending': return 'pending';
          case 'Failed': return 'failed';
          case 'Stopped': return 'stopped';
          default: return 'unknown';
        }
      }
      return 'offline';
    },
    statusIcon() {
      if (this.loading) return '⏳';
      if (this.podStatus) {
        switch(this.podStatus) {
          case 'Running': return '✅';
          case 'Pending': return '⏱️';
          case 'Failed': return '❌';
          case 'Stopped': return '⏸️';
          default: return '❓';
        }
      }
      return '⭕';
    },
    statusText() {
      if (this.loading) return '处理中...';
      if (this.podStatus) {
        switch(this.podStatus) {
          case 'Running': return '云电脑运行中';
          case 'Pending': return '云电脑启动中';
          case 'Failed': return '云电脑异常';
          case 'Stopped': return '云电脑已停止';
          default: return '状态未知';
        }
      }
      return '云电脑未启动';
    }
  },
  methods: {
    toggleCollapse() {
      this.isCollapsed = !this.isCollapsed;
      // 保存状态到 localStorage
      localStorage.setItem('container-manager-collapsed', this.isCollapsed);
    },
    async callAPI(endpoint, method = 'GET', body = null) {
      // 使用组件中的 apiToken
      const token = this.apiToken;
      
      const options = {
        method: method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      if (body && method !== 'GET') {
        options.body = JSON.stringify(body);
      }
      
      console.log('[ContainerManager] API调用:', {
        url: `${this.apiUrl}${endpoint}`,
        method: method,
        token: token.substring(0, 20) + '...',
        body: body
      });
      
      try {
        const response = await fetch(`${this.apiUrl}${endpoint}`, options);
        const data = await response.json();
        
        console.log('[ContainerManager] API响应:', {
          status: response.status,
          data: data
        });
        
        return { status: response.status, data: data };
      } catch (error) {
        console.error('[ContainerManager] API错误:', error);
        return { status: 'error', data: { error: error.message } };
      }
    },
    
    async checkPodStatus(isRefresh = false) {
      // 确保有token
      if (!this.apiToken) {
        console.error('[ContainerManager] Token未配置，无法检查Pod状态');
        this.errorMessage = 'Token未配置';
        return;
      }
      
      // 如果有缓存且距离上次成功获取不到5秒，直接使用缓存
      const now = Date.now();
      if (this.cachedPodInfo && (now - this.lastSuccessfulFetch) < 5000) {
        console.log('[ContainerManager] 使用缓存的Pod信息');
        this.podInfo = this.cachedPodInfo;
        return;
      }
      
      console.log('[ContainerManager] 检查Pod状态，使用Token:', this.apiToken.substring(0, 20) + '...');
      
      // 只在刷新时显示loading，初始加载和定时刷新不显示
      if (isRefresh) {
        this.loading = true;
      }
      
      this.errorMessage = '';
      const result = await this.callAPI('/api/v1/pods');
      
      if (result.status === 200 && result.data.pods) {
        const pods = result.data.pods;
        if (pods.length > 0) {
          const podName = pods[0].name;
          console.log('[ContainerManager] Pod列表获取成功，找到Pod:', podName);
          
          // 获取Pod详细信息
          if (podName) {
            await this.getPodDetail(podName, pods[0]);
          }
        } else {
          this.podInfo = null;
          this.cachedPodInfo = null;
          console.log('[ContainerManager] 没有找到Pod');
        }
      } else if (result.status === 401) {
        this.errorMessage = 'Token无效或已过期';
        console.error('[ContainerManager] 401错误，Token可能无效:', this.apiToken.substring(0, 20) + '...');
      } else {
        console.error('[ContainerManager] 获取Pod状态失败:', result);
      }
      
      if (isRefresh) {
        this.loading = false;
      }
    },
    
    async getPodDetail(podName, basicInfo = null) {
      console.log('[ContainerManager] 获取Pod详情:', podName);
      
      const result = await this.callAPI(`/api/v1/pods/${podName}`);
      
      if (result.status === 200) {
        // 合并基本信息和详细信息，避免闪烁
        const newPodInfo = {
          ...(basicInfo || this.podInfo || {}),
          ...result.data
        };
        
        // 只有当数据真正改变时才更新，避免不必要的重新渲染
        if (JSON.stringify(this.podInfo) !== JSON.stringify(newPodInfo)) {
          this.podInfo = newPodInfo;
          // 更新缓存
          this.cachedPodInfo = newPodInfo;
          this.lastSuccessfulFetch = Date.now();
          
          // 更新全局缓存
          window.__containerManagerCache = {
            podInfo: newPodInfo,
            vncPassword: this.vncPassword,
            timestamp: Date.now()
          };
          
          console.log('[ContainerManager] Pod详情获取成功:', this.podInfo);
        }
        
        // 如果详情中包含VNC密码，保存它
        if (result.data.vnc_password && !this.vncPassword) {
          this.vncPassword = result.data.vnc_password;
        }
      } else {
        // 如果获取详情失败，至少保留基本信息
        if (basicInfo && !this.podInfo) {
          this.podInfo = basicInfo;
          // 也更新缓存
          this.cachedPodInfo = basicInfo;
          this.lastSuccessfulFetch = Date.now();
        }
        console.error('[ContainerManager] 获取Pod详情失败:', result);
      }
    },
    
    async createPod() {
      this.loading = true;
      this.errorMessage = '';
      
      const body = {
        cpu_limit: '2',
        memory_limit: '4Gi',
        storage_size: '20Gi'
      };
      
      const result = await this.callAPI('/api/v1/pods', 'POST', body);
      this.loading = false;
      
      if (result.status === 200 || result.status === 201) {
        // 保存VNC密码
        if (result.data.vnc_password) {
          this.vncPassword = result.data.vnc_password;
          
          // 显示VNC密码给用户
          this.$alert(`云电脑创建成功！<br><br>VNC密码: <strong>${result.data.vnc_password}</strong><br><br>请妥善保存此密码，用于访问VNC远程桌面。`, '创建成功', {
            dangerouslyUseHTMLString: true,
            confirmButtonText: '复制密码',
            callback: () => {
              this.copyToClipboard(result.data.vnc_password, 'VNC密码已复制');
            }
          });
        } else {
          this.$message.success('云电脑创建成功');
        }
        
        // 获取创建的Pod名称并获取详情
        if (result.data.pod_name || result.data.name) {
          const podName = result.data.pod_name || result.data.name;
          setTimeout(() => {
            this.getPodDetail(podName);
          }, 2000);
        } else {
          setTimeout(() => this.checkPodStatus(), 2000);
        }
      } else {
        this.errorMessage = result.data.message || '创建失败';
        this.$message.error(this.errorMessage);
      }
    },
    
    async performAction(action, podName) {
      this.loading = true;
      this.errorMessage = '';

      // 如果是重启操作，先停止再重新创建
      if (action === 'restart') {
        try {
          // 第一步：停止云电脑
          this.$message.info('正在停止云电脑...');
          const stopResult = await this.callAPI(`/api/v1/pods/${podName}/stop`, 'POST');

          if (stopResult.status !== 200) {
            this.errorMessage = stopResult.data.message || '停止失败';
            this.$message.error(this.errorMessage);
            this.loading = false;
            return;
          }

          this.$message.success('云电脑已停止');

          // 等待一段时间确保停止完成
          await new Promise(resolve => setTimeout(resolve, 3000));

          // 第二步：重新创建（启动）云电脑
          this.$message.info('正在启动云电脑...');
          const createBody = {
            cpu_limit: '2',
            memory_limit: '4Gi',
            storage_size: '20Gi'
          };
          const createResult = await this.callAPI('/api/v1/pods', 'POST', createBody);

          this.loading = false;

          if (createResult.status === 200 || createResult.status === 201) {
            this.$message.success('云电脑重启成功');

            // 保存VNC密码（如果有）
            if (createResult.data.vnc_password && !this.vncPassword) {
              this.vncPassword = createResult.data.vnc_password;
            }

            // 获取创建的Pod详情
            if (createResult.data.pod_name || createResult.data.name) {
              const newPodName = createResult.data.pod_name || createResult.data.name;
              setTimeout(() => {
                this.getPodDetail(newPodName);
              }, 2000);
            } else {
              setTimeout(() => this.checkPodStatus(), 2000);
            }
          } else {
            this.errorMessage = createResult.data.message || '启动失败';
            this.$message.error(this.errorMessage);
          }
        } catch (error) {
          this.loading = false;
          this.errorMessage = '重启过程出错：' + error.message;
          this.$message.error(this.errorMessage);
        }
        return;
      }

      // 其他操作保持原有逻辑
      let endpoint = '';
      let method = 'POST';

      switch(action) {
        case 'stop':
          endpoint = `/api/v1/pods/${podName}/stop`;
          break;
        case 'delete':
          endpoint = `/api/v1/pods/${podName}`;
          method = 'DELETE';
          break;
      }

      const result = await this.callAPI(endpoint, method);
      this.loading = false;

      if (result.status === 200) {
        this.$message.success(`操作成功：${action}`);
        setTimeout(() => this.checkPodStatus(), 2000);
      } else {
        this.errorMessage = result.data.message || '操作失败';
        this.$message.error(this.errorMessage);
      }
    },
    
    handleCommand(command) {
      if (!this.podInfo) return;
      
      switch(command) {
        case 'vnc':
          this.openVNC();
          break;
        case 'copyVnc':
          this.copyVNCLink();
          break;
        case 'copySsh':
          this.copySSHCommand();
          break;
        case 'restart':
          this.confirmAction('restart', '确定要重启云电脑吗？');
          break;
        case 'stop':
          this.confirmAction('stop', '确定要停止云电脑吗？');
          break;
        case 'delete':
          this.confirmAction('delete', '危险！该操作将删除所有智能体任务创建保存的文件以及云电脑中的所有个人数据。确认要删除吗？');
          break;
      }
    },
    
    openVNC() {
      // 优先使用详情API返回的access_info
      if (this.podInfo?.access_info?.vnc?.novnc_url) {
        window.open(this.podInfo.access_info.vnc.novnc_url, '_blank');
      } else if (this.podInfo?.vnc_url) {
        // 兼容列表API的数据格式
        const vncUrl = this.podInfo.vnc_url
          .replace('/novnc/vnc.html', '/vnc.html?path=user/' + 
            this.podInfo.name.replace('vnc-', '') + '/websockify')
        window.open(vncUrl, '_blank');
      } else {
        this.$message.warning('VNC链接不可用');
      }
    },
    
    copyVNCLink() {
      // 优先使用详情API返回的access_info
      if (this.podInfo?.access_info?.vnc?.novnc_url) {
        this.copyToClipboard(this.podInfo.access_info.vnc.novnc_url, 'VNC链接已复制');
      } else if (this.podInfo?.vnc_url) {
        // 兼容列表API的数据格式
        const vncUrl = this.podInfo.vnc_url
          .replace('/novnc/vnc.html', '/vnc.html?path=user/' + 
            this.podInfo.name.replace('vnc-', '') + '/websockify')
        this.copyToClipboard(vncUrl, 'VNC链接已复制');
      } else {
        this.$message.warning('VNC链接不可用');
      }
    },
    
    copySSHCommand() {
      // 优先使用详情API返回的access_info
      if (this.podInfo?.access_info?.ssh?.command) {
        this.copyToClipboard(this.podInfo.access_info.ssh.command, 'SSH命令已复制');
      } else if (this.podInfo?.ssh_command) {
        // 兼容列表API的数据格式
        this.copyToClipboard(this.podInfo.ssh_command, 'SSH命令已复制');
      } else {
        this.$message.warning('SSH命令不可用');
      }
    },
    
    copyToClipboard(text, message) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      this.$message.success(message);
    },
    
    confirmAction(action, message) {
      this.$confirm(message, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        this.performAction(action, this.podInfo.name);
      }).catch(() => {
        this.$message.info('已取消操作');
      });
    },
    
    refreshStatus() {
      this.checkPodStatus(true);  // 手动刷新时传递isRefresh=true
    },
    
    formatTime(timestamp) {
      return new Date(timestamp).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    initApiToken() {
      // 优先从 sessionStorage 获取 tokenKey
      let tokenKey = sessionStorage.getItem('tokenKey');
      
      // 如果 sessionStorage 中没有，尝试从 localStorage 的 userinfo 中获取
      if (!tokenKey) {
        try {
          const userInfoStr = localStorage.getItem('userInfo');
          if (userInfoStr) {
            const userInfo = JSON.parse(userInfoStr);
            tokenKey = userInfo.apiKey || null;
            console.log('[ContainerManager] 从localStorage获取apiKey:', {
              hasApiKey: !!tokenKey,
              apiKeyPreview: tokenKey ? tokenKey.substring(0, 20) + '...' : 'none'
            });
          }
        } catch (error) {
          console.error('[ContainerManager] 解析userinfo失败:', error);
        }
      }
      
      console.log('[ContainerManager] 检查Token:', {
        hasTokenKey: !!tokenKey,
        tokenKeyPreview: tokenKey ? tokenKey.substring(0, 20) + '...' : 'none',
        currentToken: this.apiToken ? 'exists' : 'none',
        source: sessionStorage.getItem('tokenKey') ? 'sessionStorage' : 'localStorage'
      });
      
      if (tokenKey) {
        // 如果 token 没变，不需要重新初始化
        if (this.apiToken === tokenKey) {
          console.log('[ContainerManager] Token未变化，跳过初始化');
          return;
        }
        
        this.apiToken = tokenKey;
        const source = sessionStorage.getItem('tokenKey') ? 'sessionStorage' : 'localStorage';
        console.log(`[ContainerManager] Token从${source}设置成功:`, this.apiToken.substring(0, 20) + '...');
        
        // 清除之前的定时器
        if (this.checkInterval) {
          clearInterval(this.checkInterval);
        }
        
        // 立即检查状态
        this.checkPodStatus();
        
        // 设置智能刷新
        this.setupSmartRefresh();
        
        // 清除错误信息
        this.errorMessage = '';
      } else {
        console.warn('[ContainerManager] sessionStorage中没有tokenKey');
        this.errorMessage = '等待用户登录...';
      }
    },
    
    setupSmartRefresh() {
      // 清除旧的定时器
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }
      
      // 根据Pod状态动态调整刷新间隔
      this.checkInterval = setInterval(() => {
        // 如果组件被收起，减少刷新频率
        if (this.isCollapsed) {
          // 收起状态下，60秒刷新一次
          if (Date.now() - this.lastCheckTime > 60000) {
            this.checkPodStatus();
            this.lastCheckTime = Date.now();
          }
        } else {
          // 展开状态下，根据Pod状态调整刷新频率
          let interval = this.checkIntervalTime;
          
          if (this.podStatus === 'Pending') {
            // 启动中状态，10秒刷新一次
            interval = 10000;
          } else if (this.podStatus === 'Running') {
            // 运行中状态，45秒刷新一次
            interval = 45000;
          } else if (this.podStatus === 'Failed' || this.podStatus === 'Stopped') {
            // 失败或停止状态，60秒刷新一次
            interval = 60000;
          }
          
          if (Date.now() - this.lastCheckTime > interval) {
            this.checkPodStatus();
            this.lastCheckTime = Date.now();
          }
        }
      }, 5000); // 每5秒检查一次是否需要刷新
    }
  },
  mounted() {
    console.log('[ContainerManager] 组件已挂载');
    
    // 从全局缓存恢复Pod信息
    const globalCache = window.__containerManagerCache;
    if (globalCache && globalCache.podInfo) {
      const now = Date.now();
      // 如果缓存不超过30秒，直接使用
      if (now - globalCache.timestamp < 30000) {
        console.log('[ContainerManager] 从全局缓存恢复Pod信息');
        this.podInfo = globalCache.podInfo;
        this.cachedPodInfo = globalCache.podInfo;
        this.vncPassword = globalCache.vncPassword || '';
        this.lastSuccessfulFetch = globalCache.timestamp;
      }
    }
    
    // 恢复收起/展开状态
    const savedState = localStorage.getItem('container-manager-collapsed');
    if (savedState !== null) {
      this.isCollapsed = savedState === 'true';
    }
    // 立即尝试初始化
    this.initApiToken();
  },
  beforeDestroy() {
    // 保存Pod信息到全局缓存
    if (this.podInfo) {
      window.__containerManagerCache = {
        podInfo: this.podInfo,
        vncPassword: this.vncPassword,
        timestamp: this.lastSuccessfulFetch || Date.now()
      };
      console.log('[ContainerManager] Pod信息已保存到全局缓存');
    }
    
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }
  },
  watch: {
    '$store.state.userStore.userInfo': {
      handler() {
        // 当用户信息变化时（登录/登出），重新初始化 token
        console.log('[ContainerManager] userInfo变化，触发Token检查');
        this.initApiToken();
      },
      immediate: true
    }
  }
};
</script>

<style lang="scss" scoped>
.container-manager {
  position: absolute;
  top: 60px;
  right: 20px;
  width: 400px;
  max-width: 90%;
  background: #fff;
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 100;
  transition: width 0.3s ease, padding 0.3s ease;
  
  &.collapsed {
    width: auto;
    min-width: 120px;
    padding: 8px 12px;
  }
  
  .collapse-btn {
    position: absolute;
    left: -12px;
    top: 50%;
    transform: translateY(-50%);
    width: 24px;
    height: 24px;
    background: #fff;
    border: 1px solid #dcdfe6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.3s;
    
    &:hover {
      background: #f5f7fa;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    
    i {
      font-size: 12px;
      color: #606266;
    }
  }
  
  .collapsed-content {
    .collapsed-status {
      display: flex;
      align-items: center;
      gap: 8px;
      
      .status-icon {
        font-size: 18px;
      }
      
      .collapsed-text {
        font-size: 14px;
        color: #303133;
        white-space: nowrap;
      }
    }
  }
  
  .container-status {
    .status-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
      
      .status-info {
        display: flex;
        align-items: center;
        gap: 10px;
        
        .status-icon {
          font-size: 20px;
          
          &.running { color: #67c23a; }
          &.pending { color: #e6a23c; }
          &.failed { color: #f56c6c; }
          &.stopped { color: #909399; }
          &.offline { color: #c0c4cc; }
          &.loading { 
            color: #409eff;
            animation: rotate 1s linear infinite;
          }
        }
        
        .status-text {
          font-size: 14px;
          font-weight: 500;
          color: #303133;
        }
      }
      
      .status-actions {
        display: flex;
        gap: 8px;
      }
    }
    
    .pod-details {
      background: #f5f7fa;
      border-radius: 4px;
      padding: 10px;
      
      .detail-item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        font-size: 13px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .detail-label {
          color: #909399;
          min-width: 80px;
        }
        
        .detail-value {
          color: #606266;
          
          &.status-Running { color: #67c23a; }
          &.status-Pending { color: #e6a23c; }
          &.status-Failed { color: #f56c6c; }
          &.status-Stopped { color: #909399; }
        }
      }
    }
    
    .error-message {
      margin-top: 10px;
      padding: 8px 10px;
      background: #fef0f0;
      border-radius: 4px;
      color: #f56c6c;
      font-size: 13px;
      display: flex;
      align-items: center;
      gap: 6px;
    }
  }
  
  @keyframes rotate {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  
  // 过渡动画
  .fade-enter-active, .fade-leave-active {
    transition: opacity 0.3s ease;
  }
  
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }
}

@media (max-width: 768px) {
  .container-manager {
    padding: 10px;
    
    .status-header {
      flex-direction: column;
      align-items: flex-start !important;
      gap: 10px;
      
      .status-actions {
        width: 100%;
        justify-content: flex-end;
      }
    }
  }
}
</style>